import { Avatar, Box, Button, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import EditIcon from '@mui/icons-material/Edit';
import { Operation } from '../../../../networkRequest/operationsService';

interface CommandPersonnelSectionProps {
  operation: Operation;
  onEditIncidentCommanders?: () => void;
  onEditCommandStaff?: () => void;
}

const OperationAgencyOfficialsTable = (props) => {
  return (
    <TableContainer>
      <Table
        sx={{
          minWidth: 650,
          '& .MuiTableCell-head': {
            pt: 0,
            pb: 1
          }
        }}
        aria-label="simple table"
      >
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell align="left">Title</TableCell>
            <TableCell align="left">Rank</TableCell>
            <TableCell align="left">Badge</TableCell>
            <TableCell align="right" sx={{ pr: 0 }}>Assignment</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.operationAgencyOfficials.map((oao) => (
            <OperationAgencyOfficialTableRow
              key={oao.agency_official.id}
              operationAgencyOfficial={oao}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const OperationAgencyOfficialTableRow = (props) => {
  const oao = props.operationAgencyOfficial;
  return (
    <TableRow
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
    >
      <TableCell component="th" scope="row" sx={{ pl: 0 }}>
        <Box sx={{ minWidth: 180, display: 'flex', alignItems: 'center' }}>
          <Avatar
            src={oao.agency_official.img_url}
            alt={oao.agency_official.first_name}
            sx={{ width: 40, height: 40, mr: 2 }}
          />
          <Box>
            <Typography>
              {`${oao.agency_official.first_name} ${oao.agency_official.last_name}`}
            </Typography>
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              {oao.agency_official.agency.name}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell align="left">
        <Typography sx={{
          color: "text.secondary"
        }}>
          {oao.agency_official.title}
        </Typography>
      </TableCell>
      <TableCell align="left">
        <Typography sx={{
          color: "text.secondary"
        }}>
          {oao.agency_official.rank}
        </Typography>
      </TableCell>
      <TableCell align="left">
        <Typography sx={{
          color: "text.secondary"
        }}>
          {oao.agency_official.badge_number}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ pr: 0 }}>
        <Typography>
          {oao.assignment}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

export default function CommandPersonnelSection({
  operation,
  onEditIncidentCommanders,
  onEditCommandStaff
}: CommandPersonnelSectionProps) {
  const incidentCommanders =
    operation.operation_agency_officials?.filter(
      (o) => o.operation_role?.name === 'Incident Commander' && o.active !== false
    ) || [];
  const deputyIncidentCommanders =
    operation.operation_agency_officials?.filter(
      (o) => o.operation_role?.name === 'Deputy Incident Commander' && o.active !== false
    ) || [];
  const commandStaff =
    operation.operation_agency_officials?.filter(
      (o) => o.operation_role?.name === 'Command Staff' && o.active !== false
    ) || [];

  const totalCount =
    incidentCommanders.length + deputyIncidentCommanders.length + commandStaff.length;

  return (
    <Box sx={{ mt: 4 }}>
      <Stack direction="row" spacing={2} sx={{
        alignItems: "center"
      }}>
        <LocalPoliceIcon />
        <Typography variant="h5" sx={{ mr: 1 }}>
          Command Personnel
        </Typography>
        <Typography
          variant="body1"
          sx={{
            backgroundColor: 'blueGrey.50',
            px: 1,
            py: 0.5,
            borderRadius: 1
          }}>
          {totalCount}
        </Typography>
      </Stack>

      <Box sx={{ mb: 3, mt: 4, backgroundColor: 'blueGrey.50', borderRadius: '10px', p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Incident Commanders</Typography>
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={onEditIncidentCommanders}
            variant="outlined"
            sx={{
              border: '1px solid #536d8680',
              color: '#536d86',
              '& .MuiSvgIcon-root': {
                color: '#536d86'
              }
            }}>
            Edit
          </Button>
        </Box>

        <OperationAgencyOfficialsTable
          operationAgencyOfficials={[ ...incidentCommanders, ...deputyIncidentCommanders ]}
        />
      </Box>

      <Box sx={{ backgroundColor: 'blueGrey.50', borderRadius: '10px', p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Command Staff</Typography>
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={onEditCommandStaff}
            variant="outlined"
            sx={{
              border: '1px solid #536d8680',
              color: '#536d86',
              '& .MuiSvgIcon-root': {
                color: '#536d86'
              }
            }}>
            Edit
          </Button>
        </Box>

        {commandStaff.length > 0 ? (
          <OperationAgencyOfficialsTable
            operationAgencyOfficials={commandStaff}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 1
            }}>
            No command staff assigned.
          </Typography>
        )}

      </Box>
    </Box>
  );
}
